<template>
  <!--
    Hold the layout for any authenticated bootstrap until /auth/me lands. Can't
    gate on enforceMfaSetup here - the flag itself depends on state.company,
    which loads in the same window we're trying to protect.
  -->
  <app-loading v-if="isLoggedIn() && !state.user" />
  <app-page-layout v-else-if="useAppLayout">
    <router-view :key="$route.path" />
  </app-page-layout>
  <router-view v-else :key="$route.path"> </router-view>
</template>

<script>
import Cookies from 'js-cookie';
import { Userpilot } from 'userpilot';
import uuidv4 from 'uuid/v4';
import ApiClient, { isLoggedIn } from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import AppPageLayout from '@/components/AppPageLayout.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { isMfaComplete, MFA_GATE_ALLOW_LIST } from '@/utils/mfaGate';
import { shouldConfineScopedAdmin } from '@/utils/scopedAdminGate';

const BARE_LAYOUT_PATHS = ['/login/impersonate', '/login/forgot-password', '/login', '/kyc', '/employee/comms'];
const BARE_LAYOUT_ROUTE_NAMES = ['sme-post-signup', 'sme-oauth2', 'sme-init-integration', 'sme-lead-touch'];

export default {
  components: { AppLoading, AppPageLayout },
  name: 'App',
  setup() {
    const { enforceMfaSetup } = useFeatureFlags();
    return { enforceMfaSetup };
  },
  data() {
    return { state: State.state };
  },
  computed: {
    isOnBareRoute() {
      return (
        BARE_LAYOUT_PATHS.includes(this.$route.path) ||
        this.$route.path.indexOf('/register') !== -1 ||
        BARE_LAYOUT_ROUTE_NAMES.includes(this.$route.name)
      );
    },
    isMfaGated() {
      return this.enforceMfaSetup && this.state.user && !isMfaComplete(this.state.user);
    },
    isScopedAdminConfined() {
      return shouldConfineScopedAdmin({ claims: this.state.claims, routeName: this.$route.name });
    },
    useAppLayout() {
      // Use layout for profile page even when MFA gated (so it doesn't look squished)
      return !this.isOnBareRoute && (!this.isMfaGated || this.$route.name === 'profile');
    },
  },
  provide() {
    return { isMfaGated: () => this.isMfaGated };
  },
  methods: {
    isLoggedIn,
  },
  created() {
    if (Cookies.get('wagestream_device_id') == null) {
      // expires: https://httpwg.org/http-extensions/draft-ietf-httpbis-rfc6265bis.html#section-4.1.2.1
      Cookies.set('wagestream_device_id', uuidv4(), { expires: 400 });
    }
  },
  mounted: function () {
    // call auth/me every 60 seconds
    if (isLoggedIn()) {
      ApiClient.getUser();

      setTimeout(() => {
        // dont show this on SME onboard flow
        // Boot intercom
        // possible race condition with getting user
        if (State.state.user?.user_id) {
          this.$intercom?.boot({
            user_id: State.state.user.user_id,
            user_hash: State.state.credentials.intercom_user_hash,
            name: State.state.user.username,
            email: State.state.user.email,
            company_id: State.state.company.company_id,
            company: {
              name: State.state.company.name,
              id: State.state.company.company_id,
              self_funded: State.state.company.properties.funding_self,
              size: 250,
            },
            self_funded: State.state.company.properties.funding_self,
            hide_default_launcher: true,
            'application-id': 'partner-portal',
            host: document.location.hostname,
          });

          Userpilot.identify(State.state.user.user_id, {
            name: State.state.user.username,
            email: State.state.user.email,
            created_at: new Date(State.state.user.created_at).getTime(),
            company: {
              name: State.state.company.name,
              id: State.state.company.company_id,
              created_at: new Date(State.state.company.created_at).getTime(),
            },
          });

          // Front
          // console.log('FrontChat init (App.vue): '+State.state.user.email+' '+State.state.user.username+' '+State.state.credentials.front_user_hash+' '+State.state.company.name+' '+State.state.company.company_id);
          window.FrontChat?.('init', {
            chatId: 'eff9a91d417917f4d1378bcd9c9443e8',
            useDefaultLauncher: true,
            email: State.state.user.email,
            name: State.state.company.name,
            userHash: State.state.credentials.front_user_hash,
            customFields: {
              company_name: State.state.company.name,
              company_id: State.state.company.company_id,
              user_name: State.state.user.username,
            },
          });
        }
      }, 2000);
    }
    window.setInterval(() => {
      if (isLoggedIn()) {
        ApiClient.getUser();
      }
    }, process.env.VUE_APP_ME_REFRESH || 60000);
  },
  watch: {
    $route() {
      this.$intercom?.update();
      Userpilot.reload();
    },
    isMfaGated(gated) {
      // Redirect when the gate flips on mid-route - beforeEach can't catch the
      // bookmark-into-sensitive-page case where state.user was undefined at nav time.
      if (gated && !MFA_GATE_ALLOW_LIST.has(this.$route.name)) {
        this.$router.replace({ name: 'profile' });
      }
    },
    isScopedAdminConfined(confined) {
      // Mirror of the isMfaGated watcher: catches cold-load bookmark case where
      // state.claims was undefined when beforeEach ran, then hydrated by getUser().
      if (confined) {
        this.$router.replace({ name: 'recognition' });
      }
    },
  },
};
</script>

<style lang="scss">
.btn-primary.disabled:active,
.btn-primary.disabled.active,
.show > .btn-primary.dropdown-toggle {
  color: var(--palette-color-base-white);
  background-color: var(--palette-color-brand-primary-darken-10) !important;
  border-color: var(--palette-color-brand-primary-darken-10);
}

@media (min-width: 576px) {
  ol.breadcrumb > li.breadcrumb-item {
    max-width: none !important;
    &:hover {
      max-width: none !important;
    }
  }
}

/* modal */
.modal-dialog {
  header {
    border-radius: 0;
  }
}
</style>
